import { Loader2, Plus } from 'lucide-react';
import React, { useEffect, useRef } from 'react';

import DataGridMobile from './DataGridMobile';
import DataGridTable from './DataGridTable';
import EmptyState from './EmptyState';

const EMPTY_ARRAY = [];

/**
 * DataGrid - Modular Data Orchestrator with Classic Pagination.
 * Optimized for industrial environments with manual loading fallbacks.
 */
const DataGrid = (props) => {
    const {
        loading,
        loadingMore,
        data = EMPTY_ARRAY,
        emptyIcon,
        emptyTitle,
        emptyDescription,
        selectedIds = EMPTY_ARRAY,
        stickyOffset = '0px',
        onScroll,

        // Pagination Props
        onNextPage,
        onPrevPage,
        onPageSizeChange,
        pageSize = 10,
        currentPage = 1,
        totalPages = 1,
        hasMore = false,
        hasPrev = false,
        totalCount = 0
    } = props;

    const scrollRef = useRef(null);
    const loadMoreRef = useRef(null);

    // Infinite Scroll Logic
    useEffect(() => {
        if (props.disableInfiniteScroll || !hasMore || loading || loadingMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loading && !loadingMore) {
                    onNextPage?.();
                }
            },
            {
                root: scrollRef.current,
                threshold: 0,
                rootMargin: '1000px'
            }
        );

        const currentLoadRef = loadMoreRef.current;
        if (currentLoadRef) observer.observe(currentLoadRef);

        return () => {
            if (currentLoadRef) observer.unobserve(currentLoadRef);
        };
    }, [hasMore, loading, loadingMore, onNextPage, props.disableInfiniteScroll]);


    // ── 3. Render Views ──
    return (
        <div className="flex-1 flex flex-col min-h-0 min-w-0 w-full gap-0 overflow-hidden">
            {/* ── INTERNAL SCROLLABLE AREA ── */}
            <div
                ref={scrollRef}
                className="flex-1 min-h-0 overflow-y-auto scrollbar-premium relative"
                onScroll={onScroll}
            >
                <div className="w-full">
                    {loading && data.length === 0 ? (
                        /* Full Loading State (Inside Viewport) */
                        <div className="w-full flex flex-col items-center justify-center py-24 min-h-[400px]">
                            <Loader2 className="w-12 h-12 text-primary animate-spin mb-4 opacity-20" />
                            <span className="text-slate-400 font-black uppercase tracking-[0.3em] text-[10px]">Accessing Data...</span>
                        </div>
                    ) : data.length === 0 ? (
                        /* Initial Empty State */
                        <div className="p-12">
                            <EmptyState 
                                icon={emptyIcon} 
                                title={emptyTitle} 
                                description={emptyDescription} 
                                action={props.action}
                            />
                        </div>
                    ) : (
                        <>
                            {/* Table View (Desktop) */}
                            <div className={`hidden md:block transition-opacity duration-300 ${loading ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                                <DataGridTable {...props} data={data} stickyOffset={stickyOffset} />
                            </div>

                            {/* Mobile View (Cards/Grid) */}
                            <div className={`md:hidden transition-opacity duration-300 ${loading ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                                <DataGridMobile {...props} data={data} />
                            </div>

                            {/* Loading Overlay for page changes */}
                            {loading && (
                                <div className="absolute inset-0 z-[40] flex items-center justify-center bg-white/5 backdrop-blur-[1px]">
                                    <div className="bg-white/90 shadow-2xl rounded-2xl px-8 py-4 border border-slate-100 flex items-center gap-4 animate-in fade-in zoom-in duration-300">
                                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                        <span className="text-[11px] font-black text-slate-600 uppercase tracking-[0.2em]">Synchronizing...</span>
                                    </div>
                                </div>
                            )}

                            {/* Mobile Automatic Loading Sensor & Manual Fallback */}
                            {hasMore && (
                                <div className="md:hidden flex flex-col items-center justify-center p-12 gap-4">
                                    <div ref={loadMoreRef} className="flex flex-col items-center gap-2">
                                        <Loader2 className="w-5 h-5 text-accent animate-spin" />
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Synchronizing...</span>
                                    </div>
                                    
                                    {/* Manual Fallback Button - Critical for Production Robustness */}
                                    <button 
                                        onClick={() => onNextPage?.()}
                                        className="px-6 py-2 bg-white hover:bg-slate-50 text-slate-400 hover:text-primary rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-200 transition-all active:scale-95 shadow-sm flex items-center gap-2"
                                    >
                                        <Plus size={12} strokeWidth={3} />
                                        <span>Load More Manually</span>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Pagination is now handled by the parent ModuleDataView container to ensure visibility in all views */}
        </div>
    );
};

export default React.memo(DataGrid);
